/**
 * `<CalendarTimeline>` — the day/week time-axis grid: an hour rail down the
 * left and slot blocks positioned by their real start time and duration.
 *
 * Lifted out of `pages/Calendar.tsx` so the member page's Book picker can show
 * a day EXACTLY as the Calendar page shows it, rather than growing a second,
 * subtly-different rendering of the same slots. The move was byte-faithful; the
 * only behavioural change is that the two mutating interactions became
 * OPTIONAL props (see `CalendarTimelineProps`), because a booking picker must
 * not be able to reschedule somebody else's session or mint a new slot.
 *
 * Slot presentation helpers (`slotFill` / `slotChipName` / …) ride along and are
 * re-exported: they describe how a slot READS, which is the same question in
 * both places, and splitting them across two modules would guarantee drift.
 *
 * ALL times render in the STUDIO timezone via `studio-tz`, never the browser's.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { CalendarSlot } from "../lib/api";
import {
  studioTimeZone,
  isoToZonedDateKey,
  isoToZonedDatetimeInput,
  zonedDatetimeInputToIso,
} from "../lib/studio-tz";
import { useIsMobile } from "../lib/use-is-mobile";
import { isDoubleTap, type TapCandidate } from "../lib/double-tap";

/**
 * `YYYY-MM-DD` key for a synthetic DAY-CARRIER Date (grid cells + the anchor
 * — local-midnight Dates whose y/m/d IS the intended calendar day). Slot
 * INSTANTS must never come through here — bucket those with
 * `isoToZonedDateKey` so the day is the studio's, not the browser's.
 */
export function localDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", {
    timeZone: studioTimeZone(),
    hour: "2-digit",
    minute: "2-digit",
  });
}

export type SlotFill = "open" | "partial" | "full" | "inactive";

export function slotFill(s: CalendarSlot): SlotFill {
  if (s.status !== "active") return "inactive";
  if (s.taken_count >= s.capacity) return "full";
  if (s.taken_count > 0) return "partial";
  return "open";
}

/**
 * Slice 9 — a slot's LOCATION bucket for colour-coding: `studio` / `home` from
 * `slots.location`, else the first booking's `session_type` (studio/home), else
 * `unknown`. Drives the block/chip background hue (fill-state moves to a border
 * stripe), so both axes stay readable.
 */
export type SlotLocation = "studio" | "home" | "unknown";
export function slotLocation(s: CalendarSlot): SlotLocation {
  const loc = (s.location ?? "").toLowerCase();
  if (loc === "studio" || loc === "home") return loc;
  const st = (s.bookings[0]?.session_type ?? "").toLowerCase();
  if (st === "studio" || st === "home") return st;
  return "unknown";
}

/** Booked members' names on a slot (active/completed bookings carry them). */
function bookedMemberNames(s: CalendarSlot): ReadonlyArray<string> {
  return s.bookings
    .map((b) => b.member_name)
    .filter((n): n is string => n != null);
}

/** True when the chip should surface a member name (vs the trainer name). */
export function slotChipShowsMember(s: CalendarSlot): boolean {
  return s.taken_count > 0 && bookedMemberNames(s).length > 0;
}

/**
 * The name shown on a compact slot chip. A taken slot (`taken_count > 0`, i.e.
 * not "0/X") shows the booked member's name instead of the trainer's — staff
 * scanning the grid want to see WHO is in each session. Multiple bookings
 * collapse to "First Member +N". Falls back to the trainer name for open slots
 * (and the rare taken slot whose booking carries no member name).
 */
export function slotChipName(s: CalendarSlot): string {
  if (s.taken_count > 0) {
    const names = bookedMemberNames(s);
    const first = names[0];
    if (first !== undefined) {
      return names.length > 1 ? `${first} +${names.length - 1}` : first;
    }
  }
  return s.trainer_name ?? "—";
}

/** Tooltip for a slot chip — keeps the trainer discoverable when the chip
 * surfaces a member name, and always shows the fill count + hidden flag. */
export function slotChipTitle(s: CalendarSlot): string {
  const trainer = s.trainer_name ?? "Trainer";
  const fill = `${s.taken_count}/${s.capacity}`;
  const hidden = s.hidden ? " · hidden" : "";
  if (slotChipShowsMember(s)) {
    return `${bookedMemberNames(s).join(", ")} · ${trainer} · ${fill}${hidden}`;
  }
  return `${trainer} · ${fill}${hidden}`;
}

const PX_PER_MIN = 0.9;
/**
 * Vertical chrome `.cal-timeline` puts around its `gridHeight`-tall axis: 22px
 * top padding + 10px bottom + 1px borders. The element is `border-box`, so the
 * inline height must cover ALL of it — at the previous `+ 24` the box was 10px
 * SHORTER than its own content, and since the closing hour label is centred on
 * the final gridline (hanging half its height below it), that label fell
 * outside and was clipped by the computed `overflow-y`.
 */
const TIMELINE_CHROME_PX = 34;
const SNAP_MIN = 5;
/**
 * Snap for a slot CREATED by double-clicking the grid. Coarser than the
 * 5-minute drag snap on purpose: a drag nudges an existing booking's time and
 * wants precision, whereas a new slot almost always wants a round quarter-hour
 * — and the click is a rough gesture (±4px ≈ ±5 minutes at PX_PER_MIN).
 */
const CREATE_SNAP_MIN = 15;

/**
 * True when a pointer event landed on bare grid rather than on a slot block.
 * Double-clicking a BLOCK already means "open this slot", so creating a new
 * one there too would fire two conflicting intents from one gesture.
 */
function isEmptyGridTarget(t: EventTarget | null): boolean {
  return t instanceof Element && t.closest(".cal-timeline-block") === null;
}

/** Minutes-from-midnight of an ISO instant in the STUDIO zone (DST-correct). */
export function slotStartMin(iso: string): number {
  const hm = isoToZonedDatetimeInput(iso).slice(11); // "HH:mm"
  const h = Number(hm.slice(0, 2));
  const m = Number(hm.slice(3, 5));
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : 0;
}

/** Slot duration in whole minutes (floored to a sane minimum). */
export function slotDurationMin(s: CalendarSlot): number {
  const ms = Date.parse(s.ends_at) - Date.parse(s.starts_at);
  return Math.max(15, Math.round(ms / 60000));
}

interface TimelineBlock {
  readonly slot: CalendarSlot;
  readonly startMin: number;
  readonly durationMin: number;
  readonly col: number;
  readonly colCount: number;
}

/** Cluster-based greedy interval layout — overlapping slots split side-by-side. */
function layoutDay(slots: ReadonlyArray<CalendarSlot>): TimelineBlock[] {
  const items = [...slots]
    .map((s) => ({
      slot: s,
      startMin: slotStartMin(s.starts_at),
      durationMin: slotDurationMin(s),
    }))
    .sort((a, b) => a.startMin - b.startMin);
  const out: TimelineBlock[] = [];
  let cluster: Array<(typeof items)[number] & { col: number }> = [];
  let clusterEnd = -1;
  const flush = (): void => {
    if (cluster.length === 0) return;
    const colEnds: number[] = [];
    for (const it of cluster) {
      let col = colEnds.findIndex((e) => e <= it.startMin);
      if (col === -1) {
        col = colEnds.length;
        colEnds.push(it.startMin + it.durationMin);
      } else {
        colEnds[col] = it.startMin + it.durationMin;
      }
      it.col = col;
    }
    const colCount = colEnds.length;
    for (const it of cluster) {
      out.push({
        slot: it.slot,
        startMin: it.startMin,
        durationMin: it.durationMin,
        col: it.col,
        colCount,
      });
    }
    cluster = [];
    clusterEnd = -1;
  };
  for (const it of items) {
    if (cluster.length > 0 && it.startMin >= clusterEnd) flush();
    cluster.push({ ...it, col: 0 });
    clusterEnd = Math.max(clusterEnd, it.startMin + it.durationMin);
  }
  flush();
  return out;
}

interface DragState {
  readonly slotId: string;
  readonly dayKey: string; // live target day (Week hit-test); origin day in Today
  readonly startMin: number; // live snapped start
  readonly durationMin: number;
}

function fmtMinLabel(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Axis hour label — `7am`, `12pm`, `9pm`. Deliberately NOT `fmtMinLabel`: that
 * one also renders the drag ghost's time, which has to stay exact to the
 * minute, and a 5-minute snap shown as "7am" would read as the wrong time for
 * the whole drag. The axis marks whole hours, so it can afford the shorter
 * spelling — and gets a narrower column for it.
 */
function fmtHourLabel(min: number): string {
  const h24 = Math.floor(min / 60) % 24;
  const m = min % 60;
  const suffix = h24 < 12 ? "am" : "pm";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return m === 0
    ? `${h12}${suffix}`
    : `${h12}:${String(m).padStart(2, "0")}${suffix}`;
}


/** One draggable slot block. */
function TimelineBlockView({
  block,
  winStart,
  colBase,
  isDragging,
  isSelected,
  isDisabled,
  onPointerDown,
}: {
  readonly block: TimelineBlock;
  readonly winStart: number;
  /** Left/width the block occupies within its day column, as CSS %. */
  readonly colBase: { left: number; width: number };
  readonly isDragging: boolean;
  readonly isSelected: boolean;
  /** Rendered for context but not a target (a full slot in the Book picker). */
  readonly isDisabled?: boolean;
  readonly onPointerDown: (
    e: ReactPointerEvent,
    block: TimelineBlock,
  ) => void;
}): JSX.Element {
  const s = block.slot;
  const top = (block.startMin - winStart) * PX_PER_MIN;
  const height = Math.max(16, block.durationMin * PX_PER_MIN - 2);
  const fill = slotFill(s);
  const loc = slotLocation(s);
  return (
    <div
      className={`cal-timeline-block cal-tag-${fill} cal-loc-${loc}${
        s.hidden ? " cal-hidden" : ""
      }${isDragging ? " cal-timeline-block-dragging" : ""}${
        isSelected ? " cal-selected" : ""
      }${isDisabled === true ? " cal-timeline-block-disabled" : ""}`}
      style={{
        top: `${top}px`,
        height: `${height}px`,
        left: `${colBase.left}%`,
        width: `${colBase.width}%`,
      }}
      title={slotChipTitle(s)}
      onPointerDown={(e) => onPointerDown(e, block)}
    >
      <div className="cal-timeline-head">
        <span className="cal-timeline-time">{fmtTime(s.starts_at)}</span>
        <span className="cal-timeline-cap">
          {s.taken_count}/{s.capacity}
        </span>
      </div>
      <span
        className={`cal-timeline-name${
          slotChipShowsMember(s) ? " cal-slot-booked" : ""
        }`}
      >
        {slotChipName(s)}
      </span>
    </div>
  );
}

export interface CalendarTimelineProps {
  readonly days: ReadonlyArray<Date>;
  readonly slotsForDay: (key: string) => ReadonlyArray<CalendarSlot>;
  readonly winStart: number;
  readonly winEnd: number;
  /** Tap/click a block. The Calendar opens its card; the Book picker selects. */
  readonly onOpen: (slot: CalendarSlot) => void;
  /**
   * Drag a block to a new time (and, in Week, a new day). OMIT to make the grid
   * read-only — the Book picker does, because dragging there would silently
   * re-time another member's booked session while staff were choosing a slot.
   */
  readonly onReschedule?: (
    slot: CalendarSlot,
    startIso: string,
    endIso: string,
  ) => void;
  /**
   * Double-click (desktop) / double-tap (touch) on EMPTY grid space — opens
   * the Add-slot form pre-filled at the clicked day + time. `startLocal` is a
   * `datetime-local` string in STUDIO wall-clock, the same convention the form
   * and `zonedDatetimeInputToIso` already use.
   *
   * OMIT to disable slot creation (the Book picker: staff are booking INTO
   * existing availability, and a mistimed double-tap that minted a slot would
   * be a confusing way to discover that).
   */
  readonly onCreateAt?: (startLocal: string) => void;
  /**
   * Picker mode — the slot currently chosen, drawn with the same highlight
   * mobile uses for its tap-to-select step. `undefined` leaves selection
   * entirely to the component's own (mobile-only) state, which is what the
   * Calendar page wants.
   */
  readonly selectedSlotId?: string | null;
  /**
   * Picker mode — blocks for which `onOpen` must not fire (a full slot, in the
   * Book picker). They still RENDER: a day with a 09:00 that happens to be full
   * should look like a day with a 09:00, not a day with a hole in it.
   */
  readonly isSlotDisabled?: (slot: CalendarSlot) => boolean;
}

export function CalendarTimeline({
  days,
  slotsForDay,
  winStart,
  winEnd,
  onOpen,
  onReschedule,
  onCreateAt,
  selectedSlotId: controlledSelectedId,
  isSlotDisabled,
}: CalendarTimelineProps): JSX.Element {
  const gridHeight = (winEnd - winStart) * PX_PER_MIN;
  const todayKey = isoToZonedDateKey(new Date().toISOString());
  const columnsRef = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  /** First tap of a candidate double-tap (touch only). */
  const doubleTap = useRef<TapCandidate | null>(null);

  /**
   * Map a pointer position inside a day column to a studio wall-clock
   * `datetime-local` and hand it to the Add-slot form. The y→minutes maths is
   * the inverse of the `top: (startMin - winStart) * PX_PER_MIN` the blocks
   * are positioned with, so a slot created here lands exactly where it was
   * clicked.
   */
  const createFromPoint = useCallback(
    (dayKey: string, e: { clientY: number; currentTarget: EventTarget | null; target: EventTarget | null }): void => {
      // Read-only grid (the Book picker): empty space is just empty space.
      if (onCreateAt === undefined) return;
      if (!isEmptyGridTarget(e.target)) return;
      const col = e.currentTarget;
      if (!(col instanceof HTMLElement)) return;
      const raw = winStart + (e.clientY - col.getBoundingClientRect().top) / PX_PER_MIN;
      const snapped = Math.round(raw / CREATE_SNAP_MIN) * CREATE_SNAP_MIN;
      // Clamp so a click near the bottom edge cannot start a slot past the end
      // of the rendered window.
      const min = Math.max(
        winStart,
        Math.min(winEnd - CREATE_SNAP_MIN, snapped),
      );
      const hh = String(Math.floor(min / 60)).padStart(2, "0");
      const mm = String(min % 60).padStart(2, "0");
      onCreateAt(`${dayKey}T${hh}:${mm}`);
    },
    [winStart, winEnd, onCreateAt],
  );

  // Mobile drag-gating: a slot must be tapped-to-select (highlighted border)
  // before it can be dragged — this stops accidental reschedules from a stray
  // finger drag. A tap on an unselected slot selects it; a tap on the already-
  // selected slot opens its card; a drag of the selected slot reschedules.
  // Desktop keeps the original behaviour (tap opens, any slot drags).
  const isMobile = useIsMobile();
  const [ownSelectedId, setSelectedSlotId] = useState<string | null>(null);
  // A caller that passes `selectedSlotId` owns the highlight outright (picker
  // mode, where selection is the POINT and must persist on desktop too).
  // Otherwise it stays the component's mobile-only tap-to-select state.
  const controlled = controlledSelectedId !== undefined;
  const selectedSlotId = controlled ? controlledSelectedId : ownSelectedId;
  const selectedRef = useRef<string | null>(null);
  useEffect(() => {
    selectedRef.current = selectedSlotId;
  }, [selectedSlotId]);
  const isMobileRef = useRef(isMobile);
  useEffect(() => {
    isMobileRef.current = isMobile;
    // Leaving mobile drops any lingering selection (desktop has no such state).
    if (!isMobile) setSelectedSlotId(null);
  }, [isMobile]);

  // Mutable drag session (avoids re-binding window listeners each move).
  const session = useRef<{
    slot: CalendarSlot;
    originDayKey: string;
    originStartMin: number;
    durationMin: number;
    startX: number;
    startY: number;
    active: boolean;
    moved: boolean;
    target: { dayKey: string; startMin: number };
  } | null>(null);

  const hourLines: number[] = [];
  for (let m = winStart; m <= winEnd; m += 60) hourLines.push(m);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent, block: TimelineBlock): void => {
      // Left button / touch only.
      if (e.button !== 0) return;
      // Picker mode: a full slot is shown for context but is not a target.
      if (isSlotDisabled !== undefined && isSlotDisabled(block.slot)) return;
      const mobile = isMobileRef.current;
      // On mobile, only a already-selected slot is draggable; everything else
      // stays a plain tap target (and the page can still scroll under it).
      // No `onReschedule` ⇒ nothing is draggable anywhere, and every gesture
      // that would have been a drag decays into a tap.
      const draggable =
        onReschedule !== undefined &&
        (!mobile || selectedRef.current === block.slot.id);
      const dayKey = isoToZonedDateKey(block.slot.starts_at);
      session.current = {
        slot: block.slot,
        originDayKey: dayKey,
        originStartMin: block.startMin,
        durationMin: block.durationMin,
        startX: e.clientX,
        startY: e.clientY,
        active: false,
        moved: false,
        target: { dayKey, startMin: block.startMin },
      };

      const move = (ev: PointerEvent): void => {
        const s = session.current;
        if (s === null) return;
        const dx = ev.clientX - s.startX;
        const dy = ev.clientY - s.startY;
        if (Math.hypot(dx, dy) >= 4) s.moved = true;
        if (!draggable) return; // mobile + unselected: no drag; allow scroll
        if (!s.active && Math.hypot(dx, dy) < 4) return; // activation threshold
        if (!s.active) document.body.classList.add("cal-dragging");
        s.active = true;
        const deltaMin = dy / PX_PER_MIN;
        let startMin = Math.round((s.originStartMin + deltaMin) / SNAP_MIN) * SNAP_MIN;
        startMin = Math.max(winStart, Math.min(winEnd - s.durationMin, startMin));
        // Week: hit-test which day column the pointer is over.
        let dayKey = s.originDayKey;
        if (days.length > 1 && columnsRef.current !== null) {
          const rect = columnsRef.current.getBoundingClientRect();
          const colW = rect.width / days.length;
          let idx = Math.floor((ev.clientX - rect.left) / colW);
          idx = Math.max(0, Math.min(days.length - 1, idx));
          const d = days[idx];
          if (d !== undefined) dayKey = localDateKey(d);
        }
        s.target = { dayKey, startMin };
        setDrag({
          slotId: s.slot.id,
          dayKey,
          startMin,
          durationMin: s.durationMin,
        });
      };
      const up = (): void => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        document.body.classList.remove("cal-dragging");
        const s = session.current;
        session.current = null;
        setDrag(null);
        if (s === null) return;

        // A tap (no meaningful movement) — open on desktop; select-then-open
        // on mobile. The two-tap dance exists ONLY to arm a drag, so a grid
        // that cannot be dragged skips it: in picker mode one tap picks, on
        // any device.
        if (!s.moved) {
          if (mobile && onReschedule !== undefined) {
            if (selectedRef.current === s.slot.id) {
              onOpen(s.slot); // second tap on the selected slot → open card
            } else {
              setSelectedSlotId(s.slot.id); // first tap → select (highlight)
            }
          } else {
            onOpen(s.slot); // desktop: tap opens (unchanged)
          }
          return;
        }

        // A drag — only a genuinely-activated (and, on mobile, selected) drag
        // reschedules.
        if (!s.active) return;
        const { dayKey, startMin } = s.target;
        // No-op if unchanged.
        if (dayKey === s.originDayKey && startMin === s.originStartMin) return;
        const hh = String(Math.floor(startMin / 60)).padStart(2, "0");
        const mm = String(startMin % 60).padStart(2, "0");
        const startIso = zonedDatetimeInputToIso(`${dayKey}T${hh}:${mm}`);
        const endIso = new Date(
          Date.parse(startIso) + s.durationMin * 60000,
        ).toISOString();
        if (mobile) setSelectedSlotId(null); // clear selection after a move
        onReschedule?.(s.slot, startIso, endIso);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
    [days, winStart, winEnd, onReschedule, onOpen, isSlotDisabled],
  );

  return (
    <div
      className="cal-timeline"
      style={{ height: `${gridHeight + TIMELINE_CHROME_PX}px` }}
    >
      <div className="cal-timeline-axis" style={{ height: `${gridHeight}px` }}>
        {hourLines.map((m) => (
          <div
            key={m}
            className="cal-timeline-hour"
            style={{ top: `${(m - winStart) * PX_PER_MIN}px` }}
          >
            <span>{fmtHourLabel(m)}</span>
          </div>
        ))}
      </div>
      <div className="cal-timeline-cols" ref={columnsRef}>
        {days.map((d) => {
          const key = localDateKey(d);
          const isToday = key === todayKey;
          const blocks = layoutDay(slotsForDay(key));
          return (
            <div
              key={key}
              className={`cal-timeline-col${isToday ? " cal-timeline-col-today" : ""}`}
              style={{ height: `${gridHeight}px` }}
              onDoubleClick={(e) => createFromPoint(key, e)}
              // Touch has no dblclick, so the double-tap is detected by hand.
              // On `pointerup`, not `pointerdown`: a press that turns into a
              // drag must not also count as the first half of a double-tap.
              onPointerUp={(e) => {
                if (e.pointerType === "mouse") return;
                if (!isEmptyGridTarget(e.target)) return;
                const tap: TapCandidate = {
                  t: e.timeStamp,
                  x: e.clientX,
                  y: e.clientY,
                  key,
                };
                if (isDoubleTap(doubleTap.current, tap)) {
                  doubleTap.current = null;
                  createFromPoint(key, e);
                  return;
                }
                doubleTap.current = tap;
              }}>
              {days.length > 1 ? (
                <div className="cal-timeline-colhead">
                  <span className="cal-week-dow">
                    {d.toLocaleDateString("en-GB", { weekday: "short" })}
                  </span>{" "}
                  <span className="cal-week-dom">{d.getDate()}</span>
                </div>
              ) : null}
              {hourLines.map((m) => (
                <div
                  key={m}
                  className="cal-timeline-gridline"
                  style={{ top: `${(m - winStart) * PX_PER_MIN}px` }}
                />
              ))}
              {blocks.map((b) => {
                const gap = 2;
                const width = (100 - gap * (b.colCount - 1)) / b.colCount;
                const left = b.col * (width + gap);
                return (
                  <TimelineBlockView
                    key={b.slot.id}
                    block={b}
                    winStart={winStart}
                    colBase={{ left, width }}
                    isDragging={drag?.slotId === b.slot.id}
                    // Controlled (picker) selection shows on every device; the
                    // component's own selection is a mobile drag-arming step.
                    isSelected={
                      (controlled || isMobile) && selectedSlotId === b.slot.id
                    }
                    isDisabled={isSlotDisabled?.(b.slot) === true}
                    onPointerDown={onPointerDown}
                  />
                );
              })}
              {/* Live drag ghost, drawn in the target day column. */}
              {drag !== null && drag.dayKey === key ? (
                <div
                  className="cal-timeline-ghost"
                  style={{
                    top: `${(drag.startMin - winStart) * PX_PER_MIN}px`,
                    height: `${Math.max(16, drag.durationMin * PX_PER_MIN - 2)}px`,
                  }}
                >
                  <span className="cal-timeline-ghost-time">
                    {fmtMinLabel(drag.startMin)}
                  </span>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
